import React from 'react';
import { format, parseISO } from 'date-fns';
import { de as deDateFns } from 'date-fns/locale';
import { useLang } from '../LangContext';

interface TimeSlot {
  start: string;
  end: string;
}

interface AvailabilityRange {
  start: string;
  end: string;
}

interface AvailabilityEntry {
  date: string;
  hourly_slots?: TimeSlot[];
  free_slots?: TimeSlot[];
  available_ranges?: AvailabilityRange[];
  message?: string;
}

export interface SelectedSlot {
  start: string;
  end: string;
  display: string;
}

interface Props {
  selectedDay: Date | null;
  entry: AvailabilityEntry | null | undefined;
  duration: number;
  selectedSlot: SelectedSlot | null;
  onSelect: (slot: SelectedSlot) => void;
  currentStartIso?: string | null;
}

const generateAvailableHours = (availableRanges: AvailabilityRange[], duration: number): number[] => {
  if (!availableRanges) return [];
  const hours: number[] = [];
  availableRanges.forEach((range) => {
    const start = parseInt(range.start.split(':')[0], 10);
    const end = parseInt(range.end.split(':')[0], 10);
    for (let hour = start; hour <= end - duration; hour++) {
      hours.push(hour);
    }
  });
  return [...new Set(hours)].sort((a, b) => a - b);
};

const formatEndTime = (startHour: number, durationHours: number): string => {
  // Convert fractional duration (e.g. 1.5h) into HH:MM properly so
  // 10:00 + 1.5h renders as 11:30 instead of "11.5:00".
  const endTotalMin = Math.round((startHour + durationHours) * 60);
  const endH = Math.floor(endTotalMin / 60);
  const endM = endTotalMin % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
};

const SlotPicker = ({ selectedDay, entry, duration, selectedSlot, onSelect, currentStartIso }: Props) => {
  const { t, lang } = useLang();
  const locale = lang === 'de' ? deDateFns : undefined;

  if (!selectedDay) {
    return <div className="no-slots-message">{t.selectDatePrompt}</div>;
  }
  if (!entry) {
    return (
      <div className="no-slots-message">
        {t.noAvailabilityFor(format(selectedDay, 'dd.MM.yy'))}
      </div>
    );
  }
  if (entry.message) {
    return <div className="no-slots-message">{entry.message}</div>;
  }

  const availableSlots = entry.hourly_slots || entry.free_slots || [];
  const availableRanges = entry.available_ranges || [];

  if (availableSlots.length === 0 && availableRanges.length === 0) {
    return (
      <div className="no-slots-message">
        {t.noSlotsFor(duration, format(selectedDay, 'dd.MM.yy'))}
      </div>
    );
  }

  const availableHours = generateAvailableHours(availableRanges, duration);
  const dateStr = format(selectedDay, 'yyyy-MM-dd');

  // Slot that matches the appointment being rescheduled — greyed out so the
  // customer can't submit an unchanged time.
  const currentSlotKey = currentStartIso?.slice(0, 16); // "YYYY-MM-DDTHH:MM"

  const sectionId = `slot-picker-${format(selectedDay, 'yyyy-MM-dd')}`;

  return (
    <>
      <h3 id={sectionId}>
        {t.availableOn(format(selectedDay, 'd MMM', { locale }), duration)}
      </h3>
      {availableHours.length > 0 ? (
        <div className="time-slot-picker">
          <h4>{t.selectStartTime}</h4>
          <div className="hour-selector" role="group" aria-labelledby={sectionId}>
            {availableHours.map((hour) => {
              const startTime = `${hour.toString().padStart(2, '0')}:00`;
              const endTime = formatEndTime(hour, duration);
              const display = `${startTime} - ${endTime}`;
              const isSelected = !!(selectedSlot && selectedSlot.display === display);
              const isCurrent = currentSlotKey === `${dateStr}T${startTime}`;
              return (
                <button
                  key={hour}
                  className={`hour-button ${isSelected ? 'selected' : ''} ${isCurrent ? 'disabled' : ''}`}
                  disabled={isCurrent}
                  onClick={() =>
                    onSelect({
                      start: `${dateStr}T${startTime}:00`,
                      end: `${dateStr}T${endTime}:00`,
                      display,
                    })
                  }
                  aria-label={t.toTime(startTime, endTime)}
                  aria-pressed={isSelected}
                >
                  {startTime}
                </button>
              );
            })}
          </div>
          {selectedSlot && (
            <div className="selected-time-info" aria-live="polite">
              <p>{t.appointmentTime(selectedSlot.display, duration)}</p>
            </div>
          )}
        </div>
      ) : (
        <ul aria-labelledby={sectionId}>
          {availableSlots.map((slot, i) => {
            const display = `${format(parseISO(slot.start), 'HH:mm')} - ${format(
              parseISO(slot.end),
              'HH:mm',
            )}`;
            const isSelected = !!(selectedSlot && selectedSlot.start === slot.start);
            return (
              <li key={i}>
                <span className="slot-time">{display}</span>
                <button
                  onClick={() => onSelect({ start: slot.start, end: slot.end, display })}
                  disabled={isSelected}
                  className={isSelected ? 'selected-slot' : ''}
                  aria-label={t.toTime(display.split(' - ')[0], display.split(' - ')[1])}
                  aria-pressed={isSelected}
                >
                  {isSelected ? t.selectedSlot : t.selectSlot}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default SlotPicker;
